/**
 * article-collection-showcase.js
 *
 * Renders collection product carousels for .article-coll-showcase placeholders
 * emitted by main-article.liquid.
 *
 * Strategy:
 *   1. Fetch product list from /collections/{handle}/products.json
 *   2. For each product, fetch its server-rendered card HTML via
 *      /products/{handle}?view=card  (uses product.card.liquid template)
 *      This gives us identical cards to the handle-mode Liquid showcases:
 *      correct title stripping, specs line, AirReviews widget, price snippet.
 *   3. Assemble into a slider with manual scroll+button wiring (no
 *      <slider-component> — Dawn's constructor fires before innerHTML is set).
 */

(function () {
  'use strict';

  const SORT_MAP = {
    newest:       'created-descending',
    oldest:       'created-ascending',
    'price-asc':  'price-ascending',
    'price-desc': 'price-descending',
    title:        'title-ascending',
  };

  const CARET_SVG = `<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-caret" viewBox="0 0 10 6"><path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"/></svg>`;
  const ARROW_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 14 14" fill="none" aria-hidden="true" style="vertical-align:middle;margin-left:0.3em"><path d="M1 7h12M7 1l6 6-6 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

  /* ── Fetch product handles from collection ───────────────────────────── */
  async function fetchProductHandles(collectionHandle, limit, sort) {
    const sortBy = SORT_MAP[sort] || 'manual';
    const url = `/collections/${encodeURIComponent(collectionHandle)}/products.json?limit=${limit}&sort_by=${sortBy}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    return (data.products || []).map(p => p.handle);
  }

  /* ── Fetch server-rendered card HTML for one product ─────────────────── */
  async function fetchCardHtml(productHandle) {
    const url = `/products/${encodeURIComponent(productHandle)}?view=card`;
    const res = await fetch(url);
    if (!res.ok) return null;
    return res.text();
  }

  /* ── Fetch collection image for CTA card ─────────────────────────────── */
  async function fetchCollectionImage(handle) {
    try {
      const res = await fetch(`/collections/${encodeURIComponent(handle)}.json`);
      if (!res.ok) return null;
      const data = await res.json();
      if (data.collection && data.collection.image && data.collection.image.src) {
        let src = data.collection.image.src;
        const connector = src.includes('?') ? '&' : '?';
        src = src + connector + 'width=533';
        return src;
      }
      return null;
    } catch (_) { return null; }
  }

  /* ── Build CTA card HTML ─────────────────────────────────────────────── */
  function buildCtaCard(ctaUrl, ctaLabel, ctaImageSrc, isSlide, uid) {
    const imgHtml = ctaImageSrc
      ? `<img src="${ctaImageSrc}" alt="${ctaLabel.replace(/"/g, '&quot;')}" class="motion-reduce" loading="lazy">`
      : '';
    return `
      <li id="Slide-cta-${uid}" class="grid__item${isSlide ? ' slider__slide' : ''}">
        <div class="card-wrapper product-card-wrapper article-inline-showcase__cta-card underline-links-hover">
          <div class="card card--card card--media color-scheme-2 gradient" style="--ratio-percent:125.0%">
            <div class="card__inner ratio" style="--ratio-percent:125.0%">
              <div class="card__media"><div class="media media--transparent">${imgHtml}</div></div>
            </div>
            <div class="card__content" style="text-align:center">
              <div class="card__information">
                <h3 class="card__heading h5">
                  <a href="${ctaUrl}" class="full-unstyled-link">${ctaLabel}</a>
                </h3>
                <div class="article-inline-showcase__cta-subtitle">Scopri tutti i profumi ${ARROW_SVG}</div>
              </div>
            </div>
          </div>
        </div>
      </li>`;
  }

  /* ── Manual slider wiring ─────────────────────────────────────────────── */
  function wireSlider(ul, prevBtn, nextBtn, counterCurrent, counterTotal) {
    if (!ul || !prevBtn || !nextBtn) return;

    function getSlides() {
      return Array.from(ul.querySelectorAll('.slider__slide')).filter(el => el.clientWidth > 0);
    }

    function update() {
      const slides = getSlides();
      if (slides.length < 2) return;
      const offset  = slides[1].offsetLeft - slides[0].offsetLeft;
      const perPage = Math.max(1, Math.floor((ul.clientWidth - slides[0].offsetLeft) / offset));
      const pages   = slides.length - perPage + 1;
      const current = Math.round(ul.scrollLeft / offset) + 1;

      if (counterCurrent) counterCurrent.textContent = Math.min(current, pages);
      if (counterTotal)   counterTotal.textContent   = pages;

      prevBtn.disabled = ul.scrollLeft <= 0;
      nextBtn.disabled = current >= pages;
    }

    function slide(dir) {
      const slides = getSlides();
      if (slides.length < 2) return;
      const offset = slides[1].offsetLeft - slides[0].offsetLeft;
      ul.scrollBy({ left: dir * offset, behavior: 'smooth' });
    }

    prevBtn.addEventListener('click', () => slide(-1));
    nextBtn.addEventListener('click', () => slide(1));
    ul.addEventListener('scroll', update, { passive: true });
    new ResizeObserver(update).observe(ul);
    requestAnimationFrame(update);
  }

  /* ── Render one placeholder ───────────────────────────────────────────── */
  async function renderShowcase(placeholder) {
    const collection = placeholder.dataset.collection;
    const limit      = parseInt(placeholder.dataset.limit, 10) || 4;
    const sort       = placeholder.dataset.sort || '';
    const ctaUrl     = placeholder.dataset.ctaUrl || '';
    const ctaLabel   = placeholder.dataset.ctaLabel || '';
    const sectionId  = placeholder.dataset.section || 'coll';
    const index      = placeholder.dataset.index || '0';
    const uid        = `${sectionId}-${index}`;
    const sliderId   = `Slider-${uid}`;

    /* 1. Get product handles */
    let handles;
    try {
      handles = await fetchProductHandles(collection, limit, sort);
    } catch (err) {
      console.warn('[collection-showcase] fetch failed:', err);
      placeholder.innerHTML = '';
      return;
    }
    if (!handles.length) { placeholder.innerHTML = ''; return; }

    /* 2. Fetch all card HTML + CTA image in parallel */
    const hasCta = ctaUrl.length > 0;
    const cardPromises = handles.map(h => fetchCardHtml(h));
    const ctaImagePromise = hasCta
      ? fetchCollectionImage(ctaUrl.split('/').pop())
      : Promise.resolve(null);

    const [cardHtmls, ctaImageSrc] = await Promise.all([
      Promise.all(cardPromises),
      ctaImagePromise,
    ]);

    /* 3. Filter nulls */
    const validCards = cardHtmls.filter(Boolean);
    if (!validCards.length) { placeholder.innerHTML = ''; return; }

    const totalCount  = hasCta ? validCards.length + 1 : validCards.length;
    const showSlider  = totalCount > 2;
    const showDesktop = totalCount > 4;

    /* 4. Build slide <li> elements wrapping the server-rendered card HTML */
    const slidesHtml = validCards.map((html, i) => `
      <li id="Slide-${uid}-${i + 1}" class="grid__item${showSlider ? ' slider__slide' : ''}">
        ${html}
      </li>`).join('');

    const ctaHtml = hasCta ? buildCtaCard(ctaUrl, ctaLabel, ctaImageSrc, showSlider, uid) : '';

    let ulClasses = 'grid product-grid contains-card contains-card--product grid--4-col-desktop grid--2-col-tablet-down list-unstyled';
    if (showSlider) {
      ulClasses += ' slider';
      if (showDesktop) ulClasses += ' slider--desktop';
      ulClasses += ' slider--tablet grid--peek';
    }

    const buttonsHtml = showSlider ? `
      <div class="slider-buttons">
        <button type="button" class="slider-button slider-button--prev" name="previous" aria-label="Diapositiva precedente" aria-controls="${sliderId}">
          <span class="svg-wrapper">${CARET_SVG}</span>
        </button>
        <div class="slider-counter caption">
          <span class="slider-counter--current">1</span>
          <span aria-hidden="true"> / </span>
          <span class="slider-counter--total">${totalCount}</span>
        </div>
        <button type="button" class="slider-button slider-button--next" name="next" aria-label="Diapositiva successiva" aria-controls="${sliderId}">
          <span class="svg-wrapper">${CARET_SVG}</span>
        </button>
      </div>` : '';

    /* 5. Inject into placeholder */
    placeholder.innerHTML = `
      <div class="slider-mobile-gutter${showDesktop ? ' slider-component-desktop' : ''}">
        <ul id="${sliderId}" class="${ulClasses}" role="list" aria-label="Prodotti in evidenza">
          ${slidesHtml}
          ${ctaHtml}
        </ul>
        ${buttonsHtml}
      </div>`;

    placeholder.classList.add('article-coll-showcase--loaded');

    /* 6. Wire slider buttons */
    if (showSlider) {
      wireSlider(
        placeholder.querySelector(`#${sliderId}`),
        placeholder.querySelector('.slider-button--prev'),
        placeholder.querySelector('.slider-button--next'),
        placeholder.querySelector('.slider-counter--current'),
        placeholder.querySelector('.slider-counter--total')
      );
    }

    /* 7. Trigger AirReviews to initialise on the newly injected widgets */
    if (window.AirReviews && typeof window.AirReviews.init === 'function') {
      window.AirReviews.init();
    }
  }

  /* ── Boot ─────────────────────────────────────────────────────────────── */
  function init() {
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries, obs) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            renderShowcase(entry.target);
            obs.unobserve(entry.target);
          }
        });
      }, { rootMargin: '400px' });

      document.querySelectorAll('.article-coll-showcase').forEach(el => observer.observe(el));
    } else {
      document.querySelectorAll('.article-coll-showcase').forEach(renderShowcase);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
